/*
 * ContextMenuActions - Context-Menu-Aktionen und Menü-Generierung
 *
 * Baut den Kontext für das Rechtsklick-Menü auf und dispatched
 * die gewählte Aktion an Editor oder Console.
 * Enthält auch die dynamische Menü-Items-Generierung für Project Statistics.
 */

import { ctx } from './appContext.js'
import { PaneId } from './config.js'
import { handleCopy, handleCut, handlePaste, writeConsoleSelection } from './clipboard.js'
import { saveContent } from './persistence.js'
import { formatSize } from './dashboard/dateFormat.js'

/**
 * Liefert den aktiven Editor fuer Context-Menu-Aktionen.
 * Personal-Dict- bzw. Memory-Editor (wenn offen) haben Vorrang vor Haupt-Editor.
 * @returns {{editor: object, isMemory: boolean}|null}
 */
function _getActiveEditor() {
    if (ctx.personalDictEditor && ctx.personalDictEditor.isActive()
        && ctx.personalDictEditor.proseEditor
    ) {
        return { editor: ctx.personalDictEditor.proseEditor, isMemory: true }
    }
    if (ctx.memoryEditor && ctx.memoryEditor.isActive()
        && ctx.memoryEditor.proseEditor
    ) {
        return { editor: ctx.memoryEditor.proseEditor, isMemory: true }
    }
    if (ctx.editor) return { editor: ctx.editor, isMemory: false }
    return null
}

/** True wenn ein Overlay-Editor (Memory oder Personal-Dict) aktiv ist (kein saveContent-Aufruf). */
function _isMemoryActive() {
    return !!(
        (ctx.memoryEditor && ctx.memoryEditor.isActive())
        || (ctx.personalDictEditor && ctx.personalDictEditor.isActive())
    )
}

/** saveContent nur fuer Haupt-Editor — Memory speichert via renderLoop. */
function _saveIfNotMemory() {
    if (!_isMemoryActive()) saveContent()
}

/**
 * Baut den Kontext für das Context Menu auf.
 *
 * @param {object} pane - Pane-Objekt aus ctx.layout.panes
 * @returns {object} Kontext-Informationen für ContextMenu.open()
 */
export function buildContextMenuContext(pane) {
    const isConsole = pane.id === PaneId.CORE
    // Personal-Dict-Pane verhaelt sich strukturell wie der Memory-Pane:
    // Plain-Text-Wrapper ohne Sections, History, Lock oder saveContent.
    // Daher gleiche VISIBILITY-Gruppe ('memory') und gleiche Verzweigungen.
    const isMemoryPane = pane.id === PaneId.MEMORY || pane.id === PaneId.PERSONAL_DICT
    const consoleActive = isConsole && ctx.coreConsole

    // Aktiven Editor bestimmen — bei aktivem Overlay-Editor (Memory/Personal-Dict)
    // den jeweiligen ProseEditor.
    const target = _getActiveEditor()
    const ed = (target && (isMemoryPane || !isConsole))
        ? target.editor
        : null

    // Feld bestimmen
    let field = 'text'
    if (isConsole) {
        field = 'console'
    } else if (pane.id === PaneId.PERSONAL_DICT) {
        field = 'personal_dict'
    } else if (isMemoryPane) {
        field = 'memory'
    } else if (pane.id === PaneId.OUTLINE) {
        field = 'note'
    } else if (pane.id === PaneId.CONTEXT) {
        field = 'info'
    } else if (ed && ed.model.cursor) {
        field = ed.model.cursor.field || 'text'
    }

    // Separator prüfen — im Memory-Editor gibt's keine Separatoren
    let isSeparator = false
    if (!isConsole && !isMemoryPane && ed && ed.model.cursor) {
        isSeparator = ed.model.isSeparator(ed.model.cursor.paragraphId)
    }

    // Selection (Console: Input ODER Output)
    const hasInputSelection = isConsole && consoleActive && ctx.coreConsole.model.hasSelection()
    const hasOutputSelection = isConsole && consoleActive && ctx.coreConsole.model.hasOutputSelection()
    const hasSelection = isConsole
        ? (hasInputSelection || hasOutputSelection)
        : (ed && ed.model.hasSelection())

    // Navigation History — im Memory-Editor nicht relevant (VISIBILITY.memory
    // blendet die Gruppe ohnehin aus, aber sauber ist sauber).
    const hasHistoryBack = !isConsole && !isMemoryPane && ed
        && ed.input.navigationHistory.canGoBack()
    const hasHistoryForward = !isConsole && !isMemoryPane && ed
        && ed.input.navigationHistory.canGoForward()

    // Undo/Redo
    let canUndo = false, canRedo = false
    if (isConsole && consoleActive) {
        canUndo = ctx.coreConsole.model._undoStack.length > 0
        canRedo = ctx.coreConsole.model._redoStack.length > 0
    } else if (ed) {
        canUndo = ed.model.canUndo()
        canRedo = ed.model.canRedo()
    }

    // Indent/Outdent und Move
    let canIndent = false, canOutdent = false
    let canMoveUp = false, canMoveDown = false

    if (!isConsole && ed && ed.model.cursor) {
        const para = ed.model.getParagraph(ed.model.cursor.paragraphId)
        if (para) {
            // Indent/Outdent ("< Out" / "> In") spiegelt exakt Shift+Tab/Tab:
            // dieselbe Feld-Verzweigung wie _handleContextIndent, gekoppelt an
            // dieselben Prädikate wie die Tastatur. cursor.field statt pane-field,
            // damit Vorschau und Aktion garantiert dieselbe Quelle nutzen.
            const indentField = ed.model.cursor.field || 'text'
            if (isSeparator) {
                // Separator-Bridge: setSeparatorDepth(depth ± 1), Grenzen 0..6
                canOutdent = para.outlineDepth > 0
                canIndent = para.outlineDepth < 6
            } else if (indentField === 'note') {
                // Note-Feld: Outline-Tiefe (handleOutlineLeft/Right)
                canOutdent = para.outlineDepth > 0
                canIndent = para.outlineDepth < 10
            } else if (indentField === 'info') {
                // Info-Feld: Tab/Shift-Tab wirkungslos → beide bleiben false
            } else {
                // text-Feld (auch Memory): Block-Format-Tab über headingLevel/
                // listType/blockquote — erkennt auch Makro-/Tab-erzeugte Formate.
                canOutdent = ed.input._canTextTab(para, true)
                canIndent = ed.input._canTextTab(para, false)
            }

            // Move Up/Down: Vorschau exakt an die tatsächliche Bewegung koppeln
            // (canMoveAtCursor spiegelt moveAtCursor), damit das Menü genau dann
            // aktiv ist, wenn Alt+Up/Down auch etwas tut — über Section-Grenzen
            // hinweg bei Absätzen, als Geschwister-Tausch bei Outline/Sections.
            // Im Memory-Editor gibt es keine Sections (VISIBILITY blendet die
            // Gruppe ohnehin aus, aber sicher ist sicher).
            if (!isMemoryPane) {
                const moveField = ed.model.cursor.field || 'text'
                canMoveUp = ed.model.canMoveAtCursor(para.id, moveField, 'up')
                canMoveDown = ed.model.canMoveAtCursor(para.id, moveField, 'down')
            }
        }
    }

    // Section-Lock prüfen (nur Editor-Panes, nur text-Feld) — im Memory irrelevant
    let isLocked = false
    if (!isConsole && !isMemoryPane && ed && ed.model.cursor) {
        const cursorField = ed.model.cursor.field || 'text'
        if (cursorField === 'text') {
            isLocked = ed.model.isSectionLocked(ed.model.cursor.paragraphId)
        }
    }

    // Collapse-Host-State für Fold/Unfold-Gruppe und reduziertes Menü
    let canFold = false, canUnfold = false, isCollapseHost = false
    if (!isConsole && !isMemoryPane && ed && ed.model.cursor) {
        const para = ed.model.getParagraph(ed.model.cursor.paragraphId)
        if (para) {
            if (para.type === 'separator') {
                if (para.outlineDepth !== -1) {
                    if (para.sectionCollapsed) { canUnfold = true; isCollapseHost = true }
                    else canFold = true
                }
            } else if (para.isOutlineItem()) {
                if (para.activeCollapsed) { canUnfold = true; isCollapseHost = true }
                else canFold = true
            }
        }
    }

    // Link-Targets: @Link ist nur sinnvoll, wenn das Dokument Separatoren
    // enthaelt. Im Memory/Personal-Dict gibt es keine Separatoren — Item
    // wird dort eh ueber visibleIn ausgeblendet.
    const hasLinkTargets = !isConsole && !isMemoryPane && ed
        && ed.model.buildSeparatorMap().size > 0

    // Background-Section: Cursor im Story-Bible-Bereich (outlineDepth=-1).
    // Lock wird dort ausgegraut — die Background-Section soll nicht
    // versehentlich gesperrt werden.
    let isBackground = false
    if (!isConsole && !isMemoryPane && ed && ed.model.cursor) {
        const sep = ed.model.getSectionSeparator(ed.model.cursor.paragraphId)
        if (sep && sep.outlineDepth === -1) isBackground = true
    }

    return {
        field,
        isSeparator,
        isLocked,
        isCollapseHost,
        canFold,
        canUnfold,
        hasLinkTargets,
        isBackground,
        hasSelection,
        // Output-Selection in der Console = reduziertes Menu (nur Copy).
        isConsoleOutput: !!hasOutputSelection,
        hasHistoryBack,
        hasHistoryForward,
        canUndo,
        canRedo,
        canIndent,
        canOutdent,
        canMoveUp,
        canMoveDown,
        hasClipboard: true,  // System-Clipboard nicht sync prüfbar → nie ausgrauen
        cols: ctx.screen.cols,
        rows: ctx.screen.rows
    }
}

/**
 * Führt eine Context-Menu-Aktion aus.
 *
 * @param {string} actionId - Action-ID aus dem Context Menu
 */
export function dispatchContextAction(actionId) {
    const isConsole = ctx.coreConsole && ctx.layout.isConsoleActive()
    // Bei Memory aktiv: target.editor ist der Memory-ProseEditor
    const target = isConsole ? null : _getActiveEditor()
    const ed = target ? target.editor : null

    switch (actionId) {
        // Inline-Formate
        case 'bold':
            if (isConsole) ctx.coreConsole.model.applyFormat('bold')
            else if (ed) ed.model.toggleBold()
            break
        case 'italic':
            if (isConsole) ctx.coreConsole.model.applyFormat('italic')
            else if (ed) ed.model.toggleItalic()
            break
        case 'underline':
            if (isConsole) ctx.coreConsole.model.applyFormat('underline')
            else if (ed) ed.model.toggleUnderline()
            break
        case 'strikethrough':
            if (isConsole) ctx.coreConsole.model.applyFormat('strikethrough')
            else if (ed) ed.model.toggleStrikethrough()
            break
        case 'code':
            if (isConsole) ctx.coreConsole.model.applyFormat('code')
            else if (ed) ed.model.toggleCode()
            break

        // Undo/Redo
        case 'undo':
            if (isConsole) ctx.coreConsole.model.undo()
            else if (ed) ed.model.undo()
            break
        case 'redo':
            if (isConsole) ctx.coreConsole.model.redo()
            else if (ed) ed.model.redo()
            break

        // Clipboard
        case 'cut':
            if (isConsole) {
                const cutText = ctx.coreConsole.input.handleCut()
                // Console-Markdown → zusätzlich HTML (sonst Marker wörtlich in Drittapps)
                if (cutText) writeConsoleSelection(cutText)
            } else {
                handleCut()
            }
            break
        case 'copy':
            if (isConsole) {
                const copyText = ctx.coreConsole.input.handleCopy()
                if (copyText) writeConsoleSelection(copyText)
            } else {
                handleCopy()
            }
            break
        case 'paste':
            if (isConsole) {
                // HTML + Plain-Text lesen — sonst geht beim Paste aus Editor/Word/Notes
                // die Formatierung verloren (handlePaste bevorzugt HTML, fällt auf
                // markdownToRichText im Plaintext zurück). Pfad analog Cmd+V in
                // keyboardHandlers.js.
                ;(async () => {
                    let html = null
                    let text = null
                    try {
                        if (navigator.clipboard.read) {
                            try {
                                const items = await navigator.clipboard.read()
                                for (const item of items) {
                                    if (item.types.includes('text/html') && !html) {
                                        const blob = await item.getType('text/html')
                                        html = await blob.text()
                                    }
                                    if (item.types.includes('text/plain') && !text) {
                                        const blob = await item.getType('text/plain')
                                        text = await blob.text()
                                    }
                                }
                            } catch {
                                text = await navigator.clipboard.readText()
                            }
                        } else {
                            text = await navigator.clipboard.readText()
                        }
                        if (text || html) {
                            ctx.coreConsole.input.handlePaste(text || '', html)
                            ctx.screen.markDirty()
                        }
                    } catch { /* ignorieren */ }
                })()
            } else {
                handlePaste()
            }
            break
        case 'delete':
            if (isConsole) {
                // In der Console nur Selection-Löschung — keinen Paragraph (gibt's nicht).
                if (ctx.coreConsole.model.hasSelection()) {
                    ctx.coreConsole.model.takeSnapshot()
                    ctx.coreConsole.model.deleteSelection()
                    ctx.screen.markDirty()
                }
            } else if (ed) {
                if (ed.model.hasSelection()) {
                    ed.model.deleteSelection()
                } else if ((ed.model.cursor.field || 'text') === 'info') {
                    // Info-Feld (Context Pane): nur den Info-Text leeren, Absatz
                    // NICHT loeschen. Danach Cursor in den Main-Text (Pos 0) und
                    // Main-Pane aktivieren — analog zum Cmd+D-Pfad.
                    const infoParaId = ed.model.cursor.paragraphId
                    ed.model.clearField(infoParaId, 'info')
                    ed.model.setCursor(infoParaId, 0, 'text')
                    ctx.layout.setActivePane(PaneId.MAIN)
                    ctx.layout.exitNavigationMode()
                    ed.view.ensureCursorVisible(2)
                } else {
                    ed.model.deleteParagraph(ed.model.cursor.paragraphId)
                }
                _saveIfNotMemory()
            }
            break

        // Select — ganzen Absatz (text) bzw. ganzen Zuordnungsbereich (note)
        // selektieren, wie der Doppelklick auf den >-Marker. Reine Selektion,
        // kein Save. Im Memory/Console nicht verfuegbar (visibleIn blendet aus).
        case 'select':
            if (ed && !target.isMemory) {
                const res = ed.selectCurrentParagraph()
                if (res === 'activateMain') {
                    ctx.layout.setActivePane(PaneId.MAIN)
                    ctx.layout.exitNavigationMode()
                }
            }
            break

        // Block-Formate (Toggle)
        case 'heading':
            if (ed) _toggleBlockFormat('heading')
            break
        case 'bullet':
            if (ed) _toggleBlockFormat('bullet')
            break
        case 'numbered':
            if (ed) _toggleBlockFormat('numbered')
            break
        case 'blockquote':
            if (ed) _toggleBlockFormat('blockquote')
            break
        case 'raw':
            // Raw = Block-Code-Toggle (`para.codeBlock`). User-Label 'Raw',
            // intern 'raw' (frueher 'plain' — umbenannt, weil Mono-'Plain'
            // im Prop-Mode zu breit war).
            if (ed) _toggleBlockFormat('raw')
            break

        // Navigation History — im Memory-Editor nicht relevant
        case 'back':
            if (ed && !target.isMemory) ed.input._navigateBack()
            break
        case 'forward':
            if (ed && !target.isMemory) ed.input._navigateForward()
            break

        // @Link: oeffnet den LinkPicker programmatisch (kein '@' im Text).
        // Nur im Haupt-Editor — im Memory/Personal-Dict gibt's keinen
        // LinkPicker, und das VISIBILITY-Routing blendet das Item dort eh aus.
        case 'link':
            if (ed && !target.isMemory && ed.input._linkPicker) {
                ed.input._linkPicker.open({ triggerCharPresent: false })
            }
            break

        // Indent/Outdent — kontextabhängig
        case 'indent':
            if (ed) _handleContextIndent(false)
            break
        case 'outdent':
            if (ed) _handleContextIndent(true)
            break

        // Absatz verschieben — im Memory keine Sections
        case 'moveUp':
            if (ed && !target.isMemory) _handleContextMove('up')
            break
        case 'moveDown':
            if (ed && !target.isMemory) _handleContextMove('down')
            break

        // Separator einfügen — im Memory verboten
        case 'break':
            if (ed && !target.isMemory) {
                ed.model.insertSeparator()
                saveContent()
            }
            break

        // Fold/Unfold — Section-Collapse auf Separator bzw. Active-Collapse
        // (Im Memory nicht verfuegbar — VISIBILITY blendet die Gruppe aus.)
        case 'fold':
        case 'unfold':
            if (ed && !target.isMemory) {
                ed.input._handleCollapseToggle()
                saveContent()
            }
            break

        // Lock — togglet Section-Lock (Cmd+L im Editor entspricht).
        // Direkt setzen, kein Undo — toggleSectionLock geht selbst keinen
        // COW-Pfad. Im Memory nicht verfuegbar (keine Sections).
        case 'lock':
            if (ed && !target.isMemory && ed.model.cursor) {
                ed.model.toggleSectionLock(ed.model.cursor.paragraphId)
                saveContent()
            }
            break
    }

    ctx.layout.isDirty = true
    ctx.screen.markDirty()
}

/**
 * Toggled ein Block-Format (Heading/Bullet/Numbered/Blockquote).
 * Bei Multi-Paragraph-Selection auf alle Absätze anwenden.
 */
function _toggleBlockFormat(targetType) {
    const target = _getActiveEditor()
    if (!target) return
    const ed = target.editor

    const paraIds = _getSelectedParagraphIds()

    for (const id of paraIds) {
        const p = ed.model.getParagraph(id)
        if (!p || p.type === 'separator') continue

        switch (targetType) {
            case 'heading':
                ed.model.setBlockFormat(id, p.headingLevel > 0
                    ? { headingLevel: 0, type: 'normal' }
                    : { headingLevel: 1, type: 'heading' })
                break
            case 'bullet':
                ed.model.setBlockFormat(id, p.listType === 'bullet'
                    ? { listType: null, listLevel: 0, type: 'normal' }
                    : { listType: 'bullet', listLevel: 0, type: 'list' })
                break
            case 'numbered':
                ed.model.setBlockFormat(id, p.listType === 'numbered'
                    ? { listType: null, listLevel: 0, type: 'normal' }
                    : { listType: 'numbered', listLevel: 0, type: 'list' })
                break
            case 'blockquote':
                ed.model.setBlockFormat(id, p.blockquote
                    ? { blockquote: false, type: 'normal' }
                    : { blockquote: true, type: 'blockquote' })
                break
            case 'raw':
                // Raw togglet codeBlock. Beim Einschalten werden die anderen
                // Block-Formate explizit deaktiviert, damit ein Absatz nicht
                // gleichzeitig codeBlock+heading/list/quote ist. Beim
                // Ausschalten reicht codeBlock=false.
                ed.model.setBlockFormat(id, p.codeBlock
                    ? { codeBlock: false, type: 'normal' }
                    : {
                        codeBlock: true,
                        type: 'normal',
                        headingLevel: 0,
                        listType: null,
                        listLevel: 0,
                        blockquote: false
                    })
                break
        }
    }

    if (!target.isMemory) saveContent()
}

/**
 * Gibt die IDs aller Absätze in der aktuellen Selection zurück.
 * Ohne Selection: nur der Cursor-Absatz.
 */
function _getSelectedParagraphIds() {
    const target = _getActiveEditor()
    if (!target) return []
    const ed = target.editor
    if (!ed.model.cursor) return []

    if (!ed.model.hasSelection()) {
        return [ed.model.cursor.paragraphId]
    }

    // Selection-Range: alle Absätze zwischen Anchor und Cursor
    const sel = ed.model.selection
    if (!sel) return [ed.model.cursor.paragraphId]

    const anchorIdx = ed.model.getParagraphIndex(sel.anchor.paragraphId)
    const cursorIdx = ed.model.getParagraphIndex(sel.cursor.paragraphId)
    const startIdx = Math.min(anchorIdx, cursorIdx)
    const endIdx = Math.max(anchorIdx, cursorIdx)

    const ids = []
    for (let i = startIdx; i <= endIdx; i++) {
        const p = ed.model.getParagraphAt(i)
        if (p) ids.push(p.id)
    }
    return ids
}

/**
 * Kontextabhängiges Indent/Outdent — delegiert an die passende Handler-Methode.
 */
function _handleContextIndent(isShiftTab) {
    const target = _getActiveEditor()
    if (!target) return
    const ed = target.editor
    if (!ed.model.cursor) return
    const field = ed.model.cursor.field || 'text'
    const para = ed.model.getParagraph(ed.model.cursor.paragraphId)
    if (!para) return

    if (para.type === 'separator') {
        // Separator-Tiefe ändern (im Memory irrelevant — keine Separatoren).
        // Delegiert an dieselbe Bridge wie Tab/Shift+Tab über die Tastatur.
        if (isShiftTab) {
            ed.input._separatorBridge.onShiftTab()
        } else {
            ed.input._separatorBridge.onTab()
        }
    } else if (field === 'note') {
        ed.input._handleNoteTab(isShiftTab)
    } else {
        ed.input._handleTextTab(isShiftTab)
    }

    ed.view.ensureCursorVisible()
    if (!target.isMemory) saveContent()
}

/**
 * Absatz nach oben/unten verschieben — kontextabhängig (Text vs. Note).
 */
function _handleContextMove(direction) {
    const target = _getActiveEditor()
    if (!target) return
    const ed = target.editor
    if (!ed.model.cursor) return
    const field = ed.model.cursor.field || 'text'

    // Einheitliche Verzweigung (Separator → Section, Note → Subtree, sonst
    // Absatz) — spiegelt exakt Alt+Up/Down (EditorInput).
    const moved = ed.model.moveAtCursor(ed.model.cursor.paragraphId, field, direction)

    if (moved) {
        ed.view.ensureCursorVisible()
        if (!target.isMemory) saveContent()
    } else {
        ctx.soundManager.ping()
    }
}

/**
 * Generiert die dynamischen Menü-Items für das Project-Statistics-Menü.
 * Reine Anzeige: Statistiken und Timestamps zur Laufzeit berechnet.
 *
 * Stats zaehlen nur den Main-Text (Hauptdokument vor dem Background-
 * Separator) — die Story-Bible-Hilfsinhalte im Background bleiben aussen
 * vor (siehe MetadataManager.getStatistics). Der Header "Main Text Content:"
 * macht das explizit. Created/Modified kommen aus dem Project-Record
 * (IndexedDB), nicht aus dem In-Memory metadata — die DB-Timestamps sind
 * die Quelle of Truth fuer ein gespeichertes Projekt.
 *
 * @returns {Array} Menü-Items
 */
export function generateProjectStatisticsMenuItems() {
    const stats = ctx.metadata.getStatistics(ctx.editor.model)

    const paragraphs = ctx.metadata.formatNumber(stats.paragraphs)
    const words = ctx.metadata.formatNumber(stats.words)
    const charsWithSpaces = ctx.metadata.formatNumber(stats.charactersWithSpaces)
    const charsWithoutSpaces = ctx.metadata.formatNumber(stats.charactersWithoutSpaces)

    const project = ctx.projectManager && ctx.projectManager.getCurrentProject
        ? ctx.projectManager.getCurrentProject()
        : null
    const created = ctx.metadata.formatTimestamp(project ? project.createdAt : null)
    const modified = ctx.metadata.formatTimestamp(project ? project.updatedAt : null)
    // Project-Size: gleiche KB/MB-Formatierung wie im Dashboard. Bei
    // 0/missing liefert formatSize '' — Fallback auf "< 1 KB", damit die
    // Zeile in einer single-row Anzeige nicht leer wirkt.
    const sizeBytes = project ? project.sizeBytes || 0 : 0
    const sizeText = formatSize(sizeBytes) || '< 1 KB'

    return [
        { key: '*', label: 'Main Text Content', isInfo: true, isHeader: true },
        { key: '-', isDivider: true },
        { key: '1', label: 'Paragraphs:', value: paragraphs, isInfo: true },
        { key: '2', label: 'Words:', value: words, isInfo: true },
        { key: '3', label: 'Characters:', value: charsWithSpaces, isInfo: true },
        { key: '4', label: 'w/o spaces:', value: charsWithoutSpaces, isInfo: true },
        { key: '-', isDivider: true },
        { key: '5', label: 'Created:', value: created, isInfo: true },
        { key: '6', label: 'Modified:', value: modified, isInfo: true },
        { key: '7', label: 'Project Size:', value: sizeText, isInfo: true }
    ]
}
